var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Button } from '@paysera/ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import { useSubmitAdditionalBorrowerInfoMutation } from '@app/api/additionalBorrowerInfoApi';
import { useLazyGetFunctionalitiesQuery } from '@app/api/functionalitiesApi';
import { useCreateLoanApplicationMutation, useGetActiveLoansQuery } from '@app/api/loanApplicationsApi';
import { useFetchCurrentSolvencyDeclarationQuery } from '@app/api/solvencyDeclarationApi';
import ClosingTime from '@app/components/alerts/ClosingTime';
import { PageLoader } from '@app/components/PageLoader';
import ApplicationIncomplete from '@app/components/popups/ApplicationIncomplete';
import Stepper from '@app/components/stepper/Stepper';
import { ACTIVE_LOAN_APPLICATION_STATUSES } from '@app/configs/loanApplication';
import { ROUTES } from '@app/configs/routes';
import { isLocalExpirationWithin30Minutes } from '@app/utils/time';
import Agreement from '@modules/borrower/components/loanApplication/Agreement';
import { useNavigationBlocker } from '@modules/borrower/hooks/useNavigationBlocker';
import { loanApplicationSchema } from '@modules/borrower/validation/loanApplicationSchema';
import LoanCalculator from '@modules/calculator/components/LoanCalculator';
import AdditionalBorrowerInfoForm from './AdditionalBorrowerInfoForm';
const fieldsToValidateCalculator = [
    'isGeneralConditionsAccepted',
    'isPreContractualInformationAccepted',
];
const fieldsToValidateAdditionalInfo = ['email', 'phoneNumber', 'address', 'accountNumber'];
const LoanApplication = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { data: loanApplications = [], isLoading: loadingLoanApplication, isFetching: fetchingLoanApplication, } = useGetActiveLoansQuery();
    const hasActiveLoan = loanApplications.some((loan) => ACTIVE_LOAN_APPLICATION_STATUSES.includes(loan.status));
    useEffect(() => {
        if (hasActiveLoan) {
            navigate(ROUTES.home);
        }
    }, [hasActiveLoan]);
    const [submitAdditionalBorrowerInfo, { isLoading: submittingBorrowerInfo }] = useSubmitAdditionalBorrowerInfoMutation();
    const [createLoanApplication, { isLoading: creatingLoan }] = useCreateLoanApplicationMutation();
    const { data: solvencyDeclaration } = useFetchCurrentSolvencyDeclarationQuery();
    const [updateFunctionalities, { isLoading: isFetchingFunctionalities }] = useLazyGetFunctionalitiesQuery();
    const isExpirationLessThan30Minutes = isLocalExpirationWithin30Minutes(solvencyDeclaration === null || solvencyDeclaration === void 0 ? void 0 : solvencyDeclaration.expiresAt);
    const [isApplicationIncomplete, setIsApplicationIncomplete] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [allowNavigation, setAllowNavigation] = useState(false);
    const [amount, setAmount] = useState(0);
    const [period, setPeriod] = useState(0);
    const [activeStep, setActiveStep] = useState(1);
    const [isValidCalculator, setIsValidCalculator] = useState(false);
    const [steps, setSteps] = useState([
        { step: 1, text: t('financing.loan_application.step_1'), completed: false },
        { step: 2, text: t('financing.loan_application.step_2'), completed: false },
        { step: 3, text: t('financing.loan_application.step_3'), completed: false },
    ]);
    const loanApplicationForm = useForm({
        resolver: yupResolver(loanApplicationSchema),
    });
    const { getValues, trigger } = loanApplicationForm;
    const setStep = (step) => {
        setActiveStep(step);
        setSteps((prevSteps) => prevSteps.map((item) => (Object.assign(Object.assign({}, item), { completed: item.step < step }))));
    };
    const handleGoToStep = (step) => {
        setStep(step);
    };
    const handleGoToPreviousStep = () => {
        setActiveStep((curStep) => curStep - 1);
        setSteps((prevSteps) => prevSteps.map((step, stepIndex) => {
            if (stepIndex === activeStep - 2) {
                return Object.assign(Object.assign({}, step), { completed: false });
            }
            return step;
        }));
    };
    const handleOpenIncompleteModal = () => {
        setIsApplicationIncomplete(true);
    };
    const navigationBlocker = useNavigationBlocker({
        isBlocked: !allowNavigation && !isSubmitting,
        onBlock: handleOpenIncompleteModal,
    });
    const handleCloseIncompleteModalWithBlocker = () => {
        setIsApplicationIncomplete(false);
        navigationBlocker.reset();
        setAllowNavigation(false);
    };
    const handleGoToLoansWithBlocker = () => {
        setAllowNavigation(true);
        updateFunctionalities();
        if (navigationBlocker.isBlocked) {
            navigationBlocker.proceed();
        }
        else {
            navigate(ROUTES.home);
        }
    };
    const handleGoToBorrowerInfo = () => __awaiter(void 0, void 0, void 0, function* () {
        const isValid = yield trigger(fieldsToValidateCalculator);
        if (!isValid) {
            return;
        }
        handleGoToStep(2);
    });
    const handleSubmitBorrowerInfo = () => __awaiter(void 0, void 0, void 0, function* () {
        setIsSubmitting(true);
        yield updateFunctionalities();
        const isValid = yield trigger(fieldsToValidateAdditionalInfo);
        if (!isValid) {
            setIsSubmitting(false);
            return;
        }
        const [email, phoneNumber, address, accountNumber] = getValues(fieldsToValidateAdditionalInfo);
        const updateFields = {};
        if (email) {
            updateFields.email = email;
        }
        if (phoneNumber) {
            updateFields.phoneNumber = phoneNumber;
        }
        if (address && address.trim()) {
            updateFields.address = address;
        }
        updateFields.accountNumber = accountNumber;
        submitAdditionalBorrowerInfo(updateFields).then((response) => {
            if (!response.error) {
                setIsSubmitting(false);
                handleGoToStep(activeStep + 1);
            }
            else {
                setIsSubmitting(false);
            }
        });
    });
    const handleSubmitAgreement = () => __awaiter(void 0, void 0, void 0, function* () {
        setIsSubmitting(true);
        yield updateFunctionalities();
        const isValid = yield trigger();
        if (!isValid) {
            setIsSubmitting(false);
            return;
        }
        const { isGeneralConditionsAccepted, isSpecialConditionsAccepted, isPreContractualInformationAccepted, accountNumber, } = getValues();
        createLoanApplication({
            amount,
            period,
            accountNumber,
            isGeneralConditionsAccepted,
            isSpecialConditionsAccepted,
            isPreContractualInformationAccepted,
        }).then((data) => {
            if (!data.error) {
                setIsSubmitting(false);
                setAllowNavigation(true);
                navigate(ROUTES.home);
            }
            else {
                setIsSubmitting(false);
            }
        });
    });
    if (loadingLoanApplication || fetchingLoanApplication || hasActiveLoan) {
        return <PageLoader />;
    }
    const footerButtons = (<>
            {activeStep > 1 && (<Button type="button" variant="secondary" onClick={handleGoToPreviousStep} label={t('financing.loan_application.previous_step')}/>)}
            {activeStep === 1 && (<Button type="button" variant="primary" onClick={handleGoToBorrowerInfo} disabled={!isValidCalculator} label={t('financing.loan_application.next_step')}/>)}
            {activeStep === 2 && (<Button type="button" variant="primary" onClick={handleSubmitBorrowerInfo} loading={isFetchingFunctionalities || submittingBorrowerInfo} disabled={isFetchingFunctionalities || submittingBorrowerInfo} label={t('financing.loan_application.submit')}/>)}
            {activeStep === 3 && (<Button type="button" variant="primary" onClick={handleSubmitAgreement} loading={isFetchingFunctionalities || creatingLoan} disabled={isFetchingFunctionalities || creatingLoan} label={t('financing.loan_application.agree')}/>)}
        </>);
    return (<FormProvider {...loanApplicationForm}>
            <ApplicationIncomplete title={t('financing.loan_application.incomplete_title')} warning={t('financing.loan_application_incomplete.leave_warning')} isOpen={isApplicationIncomplete} closeModal={handleCloseIncompleteModalWithBlocker} onProceed={handleGoToLoansWithBlocker}/>

            <div className="mt-4 md:mt-6">
                <div className="flex items-center justify-between mb-6">
                    <h1 className="headline-s">{t('financing.loan_application.title')}</h1>
                </div>

                {isExpirationLessThan30Minutes && (<div className="mb-4">
                        <ClosingTime expiresAt={solvencyDeclaration === null || solvencyDeclaration === void 0 ? void 0 : solvencyDeclaration.expiresAt}/>
                    </div>)}

                <Stepper activeStep={activeStep} steps={steps} goToStep={handleGoToStep} withNavigation/>

                <div className="mt-6">
                    {activeStep === 1 && (<LoanCalculator setIsValidCalculator={setIsValidCalculator} onAmountChange={setAmount} onPeriodChange={setPeriod} startAmount={amount} startPeriod={period}/>)}
                    {activeStep === 2 && <AdditionalBorrowerInfoForm />}
                    {activeStep === 3 && <Agreement period={period} amount={amount}/>}
                </div>

                <div className="mt-6 pt-4 border-t border-neutral-primary-initial flex justify-end gap-4">
                    {footerButtons}
                </div>
            </div>
        </FormProvider>);
};
export default LoanApplication;
